import { useState, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { compressImage } from '../../lib/imageUtils';
import IconNB from '../ui-nb/IconNB';

interface PhotoUploaderProps {
  orderId: string;
  bucket?: string;
  onUploadComplete?: (urls: string[]) => void;
  maxPhotos?: number;
  currentPhotoCount?: number;
}

interface UploadedPhoto {
  url: string;
  index: number;
}

export default function PhotoUploader({
  orderId,
  bucket = 'order-photos',
  onUploadComplete,
  maxPhotos = 5,
  currentPhotoCount = 0,
}: PhotoUploaderProps) {
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const remainingSlots = maxPhotos - currentPhotoCount - photos.length;

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const filesArray = Array.from(files);
    if (filesArray.length > remainingSlots) {
      setError(`Only ${remainingSlots} photo(s) can be added`);
      return;
    }

    // Validate file sizes (10MB max)
    for (const file of filesArray) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Photo exceeds 10MB limit');
        return;
      }
    }

    setIsUploading(true);
    setError(null);
    setProgress(0);

    const newUrls: string[] = [];
    let failedCount = 0;

    for (let i = 0; i < filesArray.length; i++) {
      try {
        const compressed = await compressImage(filesArray[i]);
        const filename = `${Date.now()}-${i}.jpg`;
        const storagePath = `${orderId}/${filename}`;

        const { error: uploadError } = await supabase.storage
          .from(bucket)
          .upload(storagePath, compressed, {
            contentType: 'image/jpeg',
            upsert: false,
          });

        if (uploadError) {
          failedCount++;
          continue;
        }

        const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(storagePath);
        newUrls.push(urlData.publicUrl);
        setPhotos(prev => [...prev, { url: urlData.publicUrl, index: i }]);
      } catch (err) {
        failedCount++;
      }

      setProgress(Math.round(((i + 1) / filesArray.length) * 100));
    }

    setIsUploading(false);
    setProgress(null);

    if (failedCount > 0 && failedCount === filesArray.length) {
      setError('All photos failed to upload');
    } else if (failedCount > 0) {
      setError(`${failedCount} photo(s) failed to upload`);
    }

    if (newUrls.length > 0) {
      onUploadComplete?.(newUrls);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [orderId, bucket, remainingSlots, onUploadComplete]);

  return (
    <div className="space-y-3">
      <div
        className="border-2 border-dashed border-black p-4 text-center cursor-pointer hover:border-nb-yellow"
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={handleFileChange}
          className="hidden"
          disabled={remainingSlots <= 0 || isUploading}
        />
        <IconNB name="camera" size={32} className="mx-auto mb-2 text-nb-text-muted" />
        <p className="text-sm text-nb-text-muted">
          {isUploading ? `Uploading... ${progress}%` : `Add photos (${remainingSlots} remaining)`}
        </p>
      </div>

      {error && (
        <p className="text-sm text-nb-red">{error}</p>
      )}

      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((photo, index) => (
            <div key={index} className="aspect-square rounded-[2px] overflow-hidden border border-black">
              <img src={photo.url} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}